import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileSpreadsheet, Upload, Loader2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

// Rates Review Sheet — download Excel, upload corrections, preview per section, apply into the master.
export function RatesReviewSheetButtons({ api, onApplied }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [picked, setPicked] = useState({});

  const download = async () => {
    setBusy('dl');
    try {
      const r = await api.get('/component-rate-tables/review-sheet', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `rates_review_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    setBusy('');
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy('up'); setFile(f);
    try {
      const fd = new FormData(); fd.append('file', f);
      const { data } = await api.post('/component-rate-tables/review-sheet/preview', fd);
      setPreview(data);
      setPicked(Object.fromEntries((data.sections || []).filter(s => s.changes.length).map(s => [s.section, false])));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read the sheet'); }
    setBusy(''); if (fileRef.current) fileRef.current.value = '';
  };

  const apply = async () => {
    const secs = Object.keys(picked).filter(k => picked[k]);
    if (!secs.length) return;
    const n = preview.sections.filter(s => secs.includes(s.section)).reduce((a, s) => a + s.changes.length, 0);
    if (!window.confirm(`Apply ${n} change(s) in ${secs.join(', ')} to the master Component Rate Tables?\nA backup is taken first; Delete rows are removed permanently.`)) return;
    setBusy('apply');
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/component-rate-tables/review-sheet/apply', fd, { params: { sections: secs.join(',') } });
      toast.success(`Applied: ${data.updated} updated, ${data.deleted} deleted (backup ${data.batch_id})`);
      setPreview(null); onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
    setBusy('');
  };

  return (
    <>
      <button data-testid="rates-review-download-btn" onClick={download} disabled={!!busy}
        className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-md text-sm font-semibold disabled:opacity-60">
        {busy === 'dl' ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Download review sheet
      </button>
      <button data-testid="rates-review-upload-btn" onClick={() => fileRef.current?.click()} disabled={!!busy}
        className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md text-sm font-semibold disabled:opacity-60">
        {busy === 'up' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload review sheet
      </button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="rates-review-file-input" onChange={(e) => upload(e.target.files?.[0])} />
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rates-review-preview">
          <DialogHeader>
            <DialogTitle>Review sheet — preview changes</DialogTitle>
            <DialogDescription>Tick the components to apply. Nothing changes until you press Apply.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(preview?.sections || []).map(s => (
              <details key={s.section} className="border border-slate-200 rounded-md" data-testid={`rates-review-section-${s.section}`}>
                <summary className="px-3 py-2 flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" disabled={!s.changes.length} checked={!!picked[s.section]} data-testid={`rates-review-pick-${s.section}`}
                    onClick={(e) => e.stopPropagation()} onChange={(e) => setPicked(p => ({ ...p, [s.section]: e.target.checked }))} />
                  <b>{s.section}</b>
                  <span className="text-slate-500">{s.changes.length} change(s)</span>
                  {s.changes.some(c => c.action === 'delete') && <span className="text-rose-700 text-xs">{s.changes.filter(c => c.action === 'delete').length} delete</span>}
                  {s.warnings.length > 0 && <span className="text-amber-700 text-xs inline-flex items-center gap-1"><AlertTriangle size={11} />{s.warnings.length} warning(s)</span>}
                </summary>
                <div className="px-3 pb-2 text-xs space-y-0.5">
                  {s.warnings.map((w, i) => <p key={`w${i}`} className="text-amber-800">⚠ {w}</p>)}
                  {s.changes.map(c => (
                    <p key={c.id} className={c.action === 'delete' ? 'text-rose-700' : 'text-slate-700'}>
                      {c.action === 'delete' ? `Delete · ${c.name}` : `${c.name} — ${c.summary}`}
                    </p>
                  ))}
                </div>
              </details>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setPreview(null)} data-testid="rates-review-cancel" className="px-3 py-1.5 text-sm border border-slate-300 rounded">Cancel</button>
            <button onClick={apply} disabled={busy === 'apply' || !Object.values(picked).some(Boolean)} data-testid="rates-review-apply"
              className="px-3 py-1.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50">
              {busy === 'apply' ? 'Applying…' : 'Apply ticked components'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
